import React, { useState } from 'react';
import { isToday } from 'date-fns';
import BEM from '../../bem';
import tzTime from '../../utils/tzTime';
import mobxInjectSelect from '../../utils/mobxInjectSelect';
import { taskActionEvent } from '../../utils/taskTracking';
import type { LogGainsightAnalytics } from '../../utils/taskTracking';
import { ReactComponent as CheckMarkIcon } from '../../../common/images/checkmark.svg';
import { ReactComponent as UserPencilIcon } from '../../../common/images/user-pencil.svg';
import { type Task, PRIORITY_LABEL } from 'js-sdk/src/types/Tasks';
import { useAppDispatch, useAppSelector, actions } from 'redux-stores';
import { useCompleteTaskMutation, useReopenTaskMutation } from 'redux-stores/tasks';

const classes = BEM.with('TaskCard');

type TaskCardProps = {
  task: Task;
  organizationId: string;
};

type MobxProps = {
  currentUserId: string;
  logGainsightAnalytics: LogGainsightAnalytics;
};

function TaskCard({
  task,
  organizationId,
  currentUserId,
  logGainsightAnalytics,
}: TaskCardProps & MobxProps) {
  const dispatch = useAppDispatch();
  const selectedTaskId = useAppSelector((state) => state.tasks.selectedTask?.id);
  const [completeTask, { isLoading: isCompleting }] = useCompleteTaskMutation();
  const [reopenTask, { isLoading: isReopening }] = useReopenTaskMutation();
  const [toggleError, setToggleError] = useState<string | null>(null);

  const isCompleted = task.status === 'completed';
  const isBusy = isCompleting || isReopening;
  const isOverdue = !isCompleted && task.due_at ? new Date(task.due_at) < new Date() : false;
  const isDueDateToday = !isCompleted && task.due_at ? isToday(new Date(task.due_at)) : false;
  const dueLabel = task.due_at ? tzTime(task.due_at).format('MMM D, h:mm A') : null;
  const completedAtLabel = task.completed_at ? tzTime(task.completed_at).format('MMM D') : null;
  const toggleComplete = async () => {
    if (isBusy) return;
    setToggleError(null);
    try {
      const updatedTask = isCompleted
        ? await reopenTask({ organizationId, taskId: task.id }).unwrap()
        : await completeTask({ organizationId, taskId: task.id }).unwrap();
      logGainsightAnalytics(
        taskActionEvent(isCompleted ? 'Reopen' : 'Complete', organizationId, currentUserId, task.id)
      );
      if (selectedTaskId === task.id) {
        dispatch(actions.updateSelectedTask(updatedTask));
      }
    } catch {
      setToggleError(isCompleted ? 'Could not reopen task.' : 'Could not complete task.');
    }
  };

  const handleCardClick = () => {
    dispatch(actions.setSelectedTask(task));
    logGainsightAnalytics(taskActionEvent('View details', organizationId, currentUserId, task.id));
  };

  return (
    <div className={classes('', { completed: isCompleted })} data-test-id="task-card">
      <div className={classes('top')}>
        {task.priority && (
          <span className={classes('priority-pill', { priority: task.priority })}>
            {PRIORITY_LABEL[task.priority]}
          </span>
        )}
        {task.source_type === 'manual' ? (
          <span className={classes('source-pill')}>
            <UserPencilIcon width={16} height={16}></UserPencilIcon>Manual
          </span>
        ) : null}
        {task.completed_at && <span className={classes('completed-at')}>{completedAtLabel}</span>}
      </div>
      <div className={classes('body')}>
        <div>
          <button
            aria-checked={isCompleted}
            aria-label={isCompleted ? 'Reopen task' : 'Complete task'}
            className={classes('checkbox', { checked: isCompleted })}
            data-test-id="task-card-checkbox"
            disabled={isBusy}
            onClick={toggleComplete}
            role="checkbox"
            type="button"
          >
            {isCompleted && <CheckMarkIcon aria-hidden className={classes('checkmark')} />}
          </button>
        </div>
        <div>
          <div className={classes('title', { completed: isCompleted })}>{task.title}</div>
          {task.description && <div className={classes('description')}>{task.description}</div>}
        </div>
      </div>
      <div className={classes('bottom')}>
        {isOverdue || isDueDateToday ? (
          <span className={classes('due-pill', { overdue: isOverdue, today: isDueDateToday })}>
            {isOverdue ? 'Overdue' : isDueDateToday ? 'Today' : null}
          </span>
        ) : null}
        {dueLabel && <span className={classes('due-pill')}>{dueLabel}</span>}
      </div>
      {toggleError && (
        <div className={classes('toggle-error')} data-test-id="task-card-error" role="alert">
          {toggleError}
        </div>
      )}
      <button
        className={classes('overlay')}
        onClick={handleCardClick}
        type="button"
        aria-label="View task details"
        data-test-id="task-card-overlay"
      />
    </div>
  );
}

export { TaskCard as TaskCardComponent };

export default mobxInjectSelect<TaskCardProps, MobxProps>({
  sessionStore: ['currentUserId'],
  trackerStore: ['logGainsightAnalytics'],
})(TaskCard);
